<script>
  import { Username, FromPage } from "./store.js";
  import { createEventDispatcher, onMount } from "svelte";
  import wrappedFetch from "../../wrappedFetch.js";

  const dispatch = createEventDispatcher();

  let code = "";
  let error = "";
  let sending = false;
  let verifying = false;
  let resendCooldown = 0;

  onMount(() => {
    if (!$Username.value) {
      dispatch("pageChange", "/signin");
      return;
    }
    sendCode();
  });

  async function sendCode() {
    sending = true;
    error = "";
    try {
      await wrappedFetch("/user/SendRecoveryCode", {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: $Username.value }),
      });
      startCooldown();
    } catch {
      error = "We couldn't send a code. Please try again shortly.";
    } finally {
      sending = false;
    }
  }

  function startCooldown() {
    resendCooldown = 120;
    const t = setInterval(() => {
      resendCooldown -= 1;
      if (resendCooldown <= 0) clearInterval(t);
    }, 1000);
  }

  async function verify() {
    error = "";
    if (!/^\d{6}$/.test(code.trim())) {
      error = "Please enter the 6-digit code from your email.";
      return;
    }
    verifying = true;
    try {
      const resp = await wrappedFetch("/user/VerifyRecoveryCode", {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: $Username.value, code: code.trim() }),
      });
      const data = await resp.json();
      if (data.success) {
        dispatch("pageChange", "/account/recreateaccount");
      } else if (data.locked) {
        error = "Too many attempts. Please restart the process.";
      } else {
        error = "That code wasn't correct or has expired.";
      }
    } catch {
      error = "Something went wrong verifying your code.";
    } finally {
      verifying = false;
    }
  }
</script>

<form on:submit|preventDefault={verify} class="box" autocomplete="off">
  <div class="field">
    <a href="/signin"><span class="fas fa-arrow-left" /> Back</a>
  </div>

  <div class="notification is-invalid">
    <p class="help" style="margin-bottom:0;">
      <strong>Verify it's you</strong><br><br>
      To protect your information, we've sent a 6-digit code to the email
      address on your account. Enter it below to continue.
  </div>

  <h1 class="title is-4">Enter your code</h1>

  <div class="field">
    <input
      class="input"
      type="text"
      inputmode="numeric"
      maxlength="6"
      autocomplete="one-time-code"
      placeholder="123456"
      bind:value={code} />
  </div>

<div class="notification" style="background-color:#eff5fb; color:#296fa8;">
  <p class="help" style="margin-bottom:0;">
    If you do not receive this email, please check your SPAM/JUNK folder.
  </p>
  <p class="help" style="margin-bottom:0;">
    The code will expire in <strong>30 minutes</strong>.
  </p>
  <p class="help" style="margin-bottom:0;">
    <strong>Please don't share this code with anyone</strong> — eAdmissions
    staff will never ask you for it.
  </p>
</div>
  {#if error}
    <div class="notification is-invalid">
      <p class="help" style="margin-bottom:0;">{error}</p>
    </div>
  {/if}

  <div style="display:flex; justify-content:space-between; align-items:center;">
    <button type="button" class="button is-text"
            on:click={sendCode}
            disabled={sending || resendCooldown > 0}>
      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
    </button>
    <button type="submit" class="button is-purple" disabled={verifying}>
      {verifying ? "Verifying…" : "Continue"}
    </button>
  </div>
</form>